import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import { FreeMode } from "swiper/modules";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import {
  useCurrentPageType,
  useResponsive,
  useTrackImpressionEventByIntersection,
} from "shared/hooks";
import type { PageType } from "shared/lib/page-type";
import { theme } from "shared/lib/styles";

import { ScmpShortsCard } from "scmp-app/components/home-v2/scmp-shorts/scmp-shorts-card";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { scmpShortsHomeV2Query$key } from "scmp-app/queries/__generated__/scmpShortsHomeV2Query.graphql";

import { Container, SectionTitle, StyledSwiper, StyledSwiperSlide } from "./styles";

type Props = {
  className?: string;
  reference: scmpShortsHomeV2Query$key;
};

export const HomeV2ScmpShorts: FunctionComponent<Props> = ({ className, reference }) => {
  const data = useFragment(
    graphql`
      fragment scmpShortsHomeV2Query on Query
      @argumentDefinitions(scmpShortsQueueName: { type: "String!" }) {
        scmpShortsV2Queue: queue(filter: { name: $scmpShortsQueueName }) {
          items(first: 5) {
            edges {
              node {
                ... on Video {
                  entityId
                  ...scmpShortsCard
                }
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const items = data.scmpShortsV2Queue?.items?.edges ?? [];
  const listOfArticles = items.map(item => item.node?.entityId).join(",");

  const currentPageType = useCurrentPageType() as
    | PageType.Homepage
    | PageType.Section
    | PageType.SubSection;

  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), false);

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles,
        page_type: currentPageType,
        widget_name: "scmp_shorts",
      },
      subcategory: "widget",
    }),
    [currentPageType, listOfArticles],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  if (items.length === 0) return null;

  return (
    <BaseLinkContextProvider customQueryParameters={{ module: tracking.module.ScmpShorts }}>
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <SectionTitle>SCMP SHORTS</SectionTitle>
        <StyledSwiper
          cssMode={false}
          freeMode
          modules={[FreeMode]}
          navigation={false}
          pagination={false}
          slidesOffsetAfter={isTabletUp ? 0 : 20}
          slidesOffsetBefore={isTabletUp ? 0 : 20}
          slidesPerGroupAuto
          slidesPerView="auto"
          spaceBetween={isTabletUp ? 20 : 24}
        >
          {items.map((item, index) => (
            <StyledSwiperSlide key={item.node?.entityId ?? index}>
              <ScmpShortsCard reference={item.node} />
            </StyledSwiperSlide>
          ))}
        </StyledSwiper>
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2ScmpShorts.displayName = "HomeV2ScmpShorts";
